import { useState } from 'react'
import { Activity, Loader2, RefreshCw, ChevronDown, ChevronRight, Rocket, Wrench, Shield, Eye, FileText, Server } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useEvents } from '@/hooks/useApi'
import { timeAgo } from '@/lib/utils'

const CATEGORY_CONFIG: Record<string, { label: string; color: string; icon: typeof Activity }> = {
  deployment: { label: 'Deploy', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Rocket },
  engineering: { label: 'Engineering', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Wrench },
  security: { label: 'Security', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Shield },
  knowledge: { label: 'Knowledge', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: FileText },
  architecture: { label: 'Architecture', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: FileText },
  audit: { label: 'Audit', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: Eye },
  system: { label: 'System', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: Server },
}

const SEVERITY_COLOR: Record<string, string> = {
  info: 'text-text-muted',
  warning: 'text-amber-400',
  error: 'text-red-400',
  critical: 'text-red-500 font-bold',
}

export function EngineeringActivityPanel() {
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState<string>('today')

  const after = timeRange === 'today'
    ? new Date(new Date().setHours(0, 0, 0, 0)).toISOString()
    : timeRange === '7d'
      ? new Date(Date.now() - 7 * 86400000).toISOString()
      : timeRange === '24h'
        ? new Date(Date.now() - 86400000).toISOString()
        : undefined

  const { data, isLoading, refetch } = useEvents(
    categoryFilter || undefined, undefined, undefined, after, undefined, 100
  )

  const events = data?.events ?? []
  const byCategory = data?.by_category ?? {}

  const correlationGroups = new Map<string, typeof events>()
  const ungrouped: typeof events = []
  for (const evt of events) {
    if (evt.correlation_id) {
      const group = correlationGroups.get(evt.correlation_id) || []
      group.push(evt)
      correlationGroups.set(evt.correlation_id, group)
    } else {
      ungrouped.push(evt)
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Engineering Activity
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            <div className="ml-auto flex items-center gap-2">
              <select className="bg-surface-secondary text-text-primary text-xs rounded px-2 py-1 border border-border" value={timeRange} onChange={e => setTimeRange(e.target.value)}>
                <option value="today">Today</option>
                <option value="24h">Last 24h</option>
                <option value="7d">Last 7 days</option>
              </select>
              <select className="bg-surface-secondary text-text-primary text-xs rounded px-2 py-1 border border-border" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                <option value="">All categories</option>
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (<option key={key} value={key}>{cfg.label}</option>))}
              </select>
              <button onClick={() => refetch()} className="p-1 rounded hover:bg-surface-secondary" title="Refresh">
                <RefreshCw className="h-3.5 w-3.5 text-text-muted" />
              </button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(byCategory).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(byCategory).map(([cat, count]) => {
                const cfg = CATEGORY_CONFIG[cat] || { label: cat, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
                return (<Badge key={cat} variant="outline" className={cfg.color + ' cursor-pointer'} onClick={() => setCategoryFilter(cat === categoryFilter ? '' : cat)}>{cfg.label}: {count}</Badge>)
              })}
              <Badge variant="outline" className="bg-surface-secondary text-text-muted border-border">Total: {events.length}</Badge>
            </div>
          )}
          {events.length === 0 && !isLoading && (<p className="text-text-muted text-sm py-4 text-center">No events found for this period.</p>)}
          {Array.from(correlationGroups.entries()).map(([corrId, group]) => {
            const isExpanded = expandedId === corrId
            const primary = group[0]
            const cfg = CATEGORY_CONFIG[primary.category] || CATEGORY_CONFIG.system
            const Icon = cfg.icon
            return (
              <div key={corrId} className="border border-border rounded-lg mb-2">
                <button className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-secondary/50 rounded-t-lg" onClick={() => setExpandedId(isExpanded ? null : corrId)}>
                  {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-text-muted" />}
                  <Icon className="h-3.5 w-3.5" />
                  <Badge variant="outline" className={cfg.color + ' text-xs'}>{primary.event_type}</Badge>
                  <span className="text-sm text-text-primary truncate">{primary.worker || primary.source}</span>
                  {primary.build_id && <span className="text-xs text-text-muted font-mono">{primary.build_id.slice(0, 8)}</span>}
                  <span className="ml-auto text-xs text-text-muted">{group.length} event{group.length > 1 ? 's' : ''}</span>
                  <span className="text-xs text-text-muted">{timeAgo(new Date(primary.timestamp))}</span>
                </button>
                {isExpanded && (<div className="px-3 pb-2 space-y-1 border-t border-border">{group.map(evt => (<EventRow key={evt.id} event={evt} />))}</div>)}
              </div>
            )
          })}
          {ungrouped.map(evt => {
            const cfg = CATEGORY_CONFIG[evt.category] || CATEGORY_CONFIG.system
            const Icon = cfg.icon
            const isExpanded = expandedId === evt.id
            return (
              <div key={evt.id} className="border border-border rounded-lg mb-2">
                <button className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-secondary/50 rounded-lg" onClick={() => setExpandedId(isExpanded ? null : evt.id)}>
                  {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-text-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-text-muted" />}
                  <Icon className="h-3.5 w-3.5" />
                  <Badge variant="outline" className={cfg.color + ' text-xs'}>{evt.event_type}</Badge>
                  <span className="text-sm text-text-primary truncate">{evt.worker || evt.module || evt.source}</span>
                  <span className="ml-auto text-xs text-text-muted">{timeAgo(new Date(evt.timestamp))}</span>
                </button>
                {isExpanded && (<div className="px-3 pb-2 border-t border-border"><EventRow event={evt} /></div>)}
              </div>
            )
          })}
        </CardContent>
      </Card>
    </div>
  )
}

function EventRow({ event }: { event: { id: string; event_type: string; category: string; severity: string; source: string; module: string | null; worker: string | null; build_id: string | null; correlation_id: string | null; timestamp: string; payload: string | null; status: string; verified: number } }) {
  let parsedPayload: Record<string, unknown> | null = null
  try { if (event.payload) parsedPayload = JSON.parse(event.payload) } catch { /* ignore */ }
  return (
    <div className="text-xs space-y-1 py-1">
      <div className="flex items-center gap-2">
        <span className={SEVERITY_COLOR[event.severity] || 'text-text-muted'}>{event.severity}</span>
        <span className="text-text-muted">source: {event.source}</span>
        {event.worker && <span className="text-text-muted">worker: {event.worker}</span>}
        {event.build_id && <span className="font-mono text-text-muted">v:{event.build_id.slice(0, 8)}</span>}
        {event.verified === 1 && <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">verified</Badge>}
      </div>
      {event.module && <div className="text-text-muted">module: {event.module}</div>}
      {parsedPayload && (
        <div className="bg-surface-secondary rounded p-2 font-mono text-[10px] overflow-x-auto">
          {Object.entries(parsedPayload).map(([k, v]) => (<div key={k}><span className="text-text-muted">{k}:</span> {JSON.stringify(v)}</div>))}
        </div>
      )}
      <div className="text-text-muted">{new Date(event.timestamp).toLocaleString()} -- {event.id}</div>
    </div>
  )
}
